import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from './SelectChannels.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { observer } from 'mobx-react-lite'
import { usePostPreviewStore } from 'components/PostPreviewModalV2/PostPreviewStoreProvider'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import { uniqBy } from 'lodash'
import { useConnectAccount } from 'components/BrandKitCreationModal/useConnectAccount'
import { Button } from 'blaze-ui/Button'
import { PostPreviewScreens } from 'components/PostPreviewModalV2/PostPreviewStore'
import { ConnectedBrandKitAccountsSelect } from 'components/PostPreviewModalV2/components/BrandKitAccountsSelect/BrandKitAccountsSelect'
import { ChannelDropdown } from 'components/PostPreviewModalV2/components/ChannelDropdown/ChannelDropdown'
import { ConnectedConnectionNeeded } from 'components/PostPreviewModalV2/components/ConnectionNeeded/ConnectionNeeded'
import { Section } from 'components/PostPreviewModalV2/components/Section/Section'
import { ButtonFooter } from 'components/Wizards/MobileWizards/Layout/ButtonFooter/ButtonFooter'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { AddChannelDrawer } from 'components/PostPreviewModalV2/components/AddChannelDrawer/AddChannelDrawer'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { PostPreviewChannelType } from 'components/PostPreviewModal/Types'
import { NonBrandKitAccounts } from 'components/PostPreviewModalV2/components/NonBrandKitAccounts/NonBrandKitAccounts'
import { BrandKitLogo } from 'screens/BrandKit/BrandKitLogo/BrandKitLogo'

export const SelectChannels = observer(() => {
  const store = usePostPreviewStore()
  const { mutateAsync: createBrandKitSource } = useCreateBrandKitSource()
  const { data: brandKits } = useBrandKits()
  const { drawersStore } = useRootStore()

  const brandKit = brandKits?.find((b) => b.id === store.selectedBrandKitId)

  const onConnect = async (accounts: ExternalIntegration[]) => {
    if (!brandKit || accounts.length === 0) return

    await createBrandKitSource({
      brandKitId: brandKit.id,
      source: { type: 'ExternalIntegration', id: accounts[0].id },
    })

    store.setSelectedAccounts(uniqBy([...store.selectedAccounts, ...accounts], 'id'))
    store.addChannel(accounts[0].provider)
  }

  const connect = useConnectAccount({ onConnect })

  const handleReview = () => store.setScreen(PostPreviewScreens.PREVIEW_SCREEN)

  return (
    <div className={Styles.selectChannels}>
      <ResponsiveDesign.Desktop>
        <div className={Styles.heading}>
          <Heading level={4}>Choose your posting channels</Heading>
          <Text variant="placeholder">Select your channels or add new ones</Text>
        </div>
      </ResponsiveDesign.Desktop>

      <ResponsiveDesign.Mobile>
        <div className={Styles.heading}>
          <Heading level={1}>Choose your posting channels</Heading>
          <Text variant="secondary">Select your channels or add new ones</Text>
        </div>
      </ResponsiveDesign.Mobile>
      <Section
        title={
          brandKit && (
            <div className={Styles.brandKitItem}>
              <BrandKitLogo brandKit={brandKit} size={20} />
              <span className={Styles.name}>{brandKit.name}</span>
            </div>
          )
        }
        actions={
          <ResponsiveDesign.Desktop>
            <ChannelDropdown
              value={[
                ...store.selectedChannels,
                providerFromChannelType[store.channel as PostPreviewChannelType] as ExternalIntegrationEnum,
              ]}
              onChange={(provider) => store.addChannel(provider)}
            />
          </ResponsiveDesign.Desktop>
        }
      >
        <ConnectedBrandKitAccountsSelect />
      </Section>

      <ConnectedConnectionNeeded onConnect={connect} />
      <NonBrandKitAccounts />

      <ResponsiveDesign.Mobile>
        <ButtonFooter>
          <div className={Styles.mobileButton}>
            <Button
              variant="tertiary"
              size="lg"
              fullWidth
              onPress={() => {
                drawersStore.openDrawer(AddChannelDrawer, {
                  onChannelAdd: (provider) => {
                    store.addChannel(provider)
                    drawersStore.closeDrawer()
                  },
                  enabledChannels: Array.from(store.selectedChannels),
                })
              }}
            >
              Add New Channel
            </Button>
          </div>

          <Button
            variant="primary"
            size="lg"
            fullWidth
            onPress={handleReview}
            isDisabled={store.selectedAccounts.length === 0}
          >
            Review Post
          </Button>
        </ButtonFooter>
      </ResponsiveDesign.Mobile>
    </div>
  )
})
